/**
 * Activities that open a portal page instead of the standard form
 * (docs/integration-portal-claude-design-proposal.md §3.6, §3.8 in the SVN repo).
 *
 * A module's Create, or a custom activity, can be set in Studio to open one of the workspace's
 * portals (`portalId` on the activity; the listing header's `createPortalId` for Create). A click
 * then opens the app's own portal route in a new tab, with the module, activity and entry in its
 * query; the bounce page (PortalLaunch.vue) launches with that context and the server binds it to
 * the portal session. The page submits that same activity through the app's own activity calls, so
 * rights, required fields and state rules are those of the standard form.
 *
 * Store-free on purpose, as portalNav.js: the form hosts pass in what they know, and the specs
 * test every branch without the app around them.
 */
import { webPortals, portalHref } from "./portalNav";

export const CREATE_ACTIVITY_ID = "create";

/**
 * Standard activities other than Create never open a portal page in Phase 1 (PD-12). The ids are
 * the canvas schema's StandardActivity enum, plus the admin feature `manage`.
 */
const NOT_A_PORTAL_ACTIVITY = Object.freeze([
  "createEditView", "edit", "view", "quickView", "delete", "changeStatus", "duplicate", "comment",
  "history", "assign", "import", "print", "export", "manage", "share",
]);

/**
 * The portal an activity is set to open, from what the server sent: `portalId` on a custom
 * activity, or the listing header's `createPortalId` for Create (standard activities arrive as a
 * list of ids, so they carry nothing themselves). Null when it opens the standard form.
 *
 * Some clicks pass only `{ id }`; the header's own copy of the activity answers for those.
 *
 * @param {object} activity  { id, portalId? }
 * @param {object} [header]  the listing header: { createPortalId?, activities? }
 */
export function activityPortalId(activity, header) {
  const a = activity || {};
  const h = header || {};
  if (a.portalId) return String(a.portalId);
  if (a.id == null || a.id === "") return null;
  if (a.id === CREATE_ACTIVITY_ID) return h.createPortalId ? String(h.createPortalId) : null;
  const listed = Array.isArray(h.activities) ? h.activities.find((x) => x && x.id === a.id) : null;
  return listed && listed.portalId ? String(listed.portalId) : null;
}

/** The ids of the entries a click acts on: `entryIds`, else the ids of `entries`. */
function entryIdsOf(ctx) {
  const list = Array.isArray(ctx.entryIds)
    ? ctx.entryIds
    : (Array.isArray(ctx.entries) ? ctx.entries.map((x) => (x && typeof x === "object" ? x.id : x)) : []);
  return list.filter((x) => x != null && x !== "").map(String);
}

/**
 * Where an activity click goes instead of the standard form:
 * <app>#/portal/{id}?ws={workspace}&module={id}&activity={id}[&entry={id}], or null when the
 * standard form opens (PD-14):
 *  - the activity has no portal, or is a standard activity other than Create;
 *  - the portal is not in this member's `workspace.portals`, or not shown on the web (an off or
 *    hidden portal is not there: the bootstrap only lists what the member may open);
 *  - the session is Run As or a demo (PL-13; webPortals lists nothing then);
 *  - more than one entry is selected (Phase 1 is one entry at a time);
 *  - there is no module to name.
 *
 * The address names the context only so the bounce page can ask for it. The server checks it at
 * launch and binds it to the session; the page never reads it from the address (PD-13).
 *
 * @param {object} activity  { id, portalId }
 * @param {object} ctx       { workspace, runAs, moduleId, entryIds | entries, location? }
 */
export function activityPortalHref(activity, ctx) {
  const a = activity || {};
  const c = ctx || {};
  const activityId = a.id == null ? "" : String(a.id);
  const portalId = a.portalId == null ? "" : String(a.portalId);
  if (!activityId || !portalId) return null;
  if (NOT_A_PORTAL_ACTIVITY.indexOf(activityId) >= 0) return null;

  const workspace = c.workspace || {};
  const portal = webPortals(workspace, { runAs: Boolean(c.runAs) }).find((p) => p.id === portalId);
  if (!portal) return null;

  const moduleId = c.moduleId == null ? "" : String(c.moduleId);
  if (!/^\d{1,10}$/.test(moduleId)) return null;

  const entries = entryIdsOf(c);
  if (entries.length > 1) return null;
  // Create starts a new entry: whatever is selected in the list is not its entry.
  const entryId = activityId === CREATE_ACTIVITY_ID ? "" : (entries[0] || "");

  const base = portalHref(portalId, c.location || null, workspace.id);
  const query = [
    "module=" + encodeURIComponent(moduleId),
    "activity=" + encodeURIComponent(activityId),
  ];
  if (entryId) query.push("entry=" + encodeURIComponent(entryId));
  return base + (base.indexOf("?") >= 0 ? "&" : "?") + query.join("&");
}

// The one return-refresh armed at a time: a second portal click replaces the first, rather than
// refreshing twice when the person comes back.
let armed = null;

/**
 * The refresh a form host makes after a submit, made instead when this tab gets the focus back
 * from the portal's tab: the list (`entry:updated`), the dashboard or the calendar, and the host's
 * own extra (`onReturn`, e.g. the detail view's list).
 */
function refreshOnReturn(win, ctx) {
  if (!win || typeof win.addEventListener !== "function") return;
  if (armed && armed.win && typeof armed.win.removeEventListener === "function") {
    armed.win.removeEventListener("focus", armed.handler);
  }
  const bus = ctx.bus || (typeof window !== "undefined" ? window.eventBus : null);
  const moduleId = ctx.moduleId;
  const routeName = ctx.routeName;
  const onReturn = typeof ctx.onReturn === "function" ? ctx.onReturn : null;
  const handler = () => {
    if (armed && armed.handler === handler) armed = null;
    if (bus && typeof bus.$emit === "function") {
      bus.$emit("entry:updated", { moduleId });
      if (routeName === "dashboard") bus.$emit("dashboardRefresh");
      else if (routeName === "calendar") bus.$emit("calendarRefresh");
    }
    if (onReturn) onReturn();
  };
  armed = { win, handler };
  win.addEventListener("focus", handler, { once: true });
}

/**
 * Open the activity's portal page instead of its standard form. True when it took over; false when
 * the caller opens the standard form, as before.
 *
 * Call it straight from the click, before any await: `window.open` runs synchronously here, while
 * the browser still counts the click, or its popup blocker stops the new tab.
 *
 * @param {object} activity  { id, portalId }: for Create, pass activityPortalId(activity, header)
 * @param {object} ctx       activityPortalHref's, plus { bus, routeName, onReturn, window }
 */
export function openActivityPortal(activity, ctx) {
  const c = ctx || {};
  const href = activityPortalHref(activity, c);
  if (!href) return false;
  const win = c.window || (typeof window !== "undefined" ? window : null);
  if (!win || typeof win.open !== "function") return false;
  win.open(href, "_blank", "noopener");
  refreshOnReturn(win, c);
  return true;
}

export default { CREATE_ACTIVITY_ID, activityPortalId, activityPortalHref, openActivityPortal };
